/**
 * Check if the param is an object
 */
export function isObject<T extends object>(value: unknown): value is T {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Check if the value is a plain object
 *
 * @param value - The value to check
 * @returns True if the value is a plain object, false otherwise
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Deep merge two or more objects.
 *
 * @param target - The target object to merge into
 * @param sources - One or more source objects to merge from
 * @returns The merged object
 */
export function mergeDeep<T extends Record<string, unknown>>(target: T, ...sources: Record<string, unknown>[]): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isPlainObject(target) && isPlainObject(source)) {
    for (const key in source) {
      if (isPlainObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key] as Record<string, unknown>, source[key] as Record<string, unknown>);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}
